/**
 * «Помощь» в «Настройках» (шестерёнка в шапке): как пользоваться и частые вопросы (22.10.2026).
 *
 * Простыми словами, со снимками настоящих экранов. Снимки лежат в
 * `public/help/` и собираются `tools/help-shots.mjs` на примере данных.
 *
 * ПРАВИЛО (CLAUDE.md, «Помощь и частые вопросы»): поменялся экран или путь,
 * о котором здесь написано, — в том же изменении поправить текст и
 * пересобрать снимки. Помощь, которая ведёт туда, чего больше нет, хуже, чем
 * её отсутствие.
 */

export interface HelpItem {
  /** Ключ — и имя снимка: `public/help/<shot>.jpg`. */
  id: string;
  title: string;
  /** Абзацы ответа. */
  text: string[];
  /** Снимок экрана, о котором речь. */
  shot?: string;
}

export interface HelpGroup { title: string; items: HelpItem[] }

/** «Как пользоваться» — коротко о главном. */
export const GUIDES: HelpItem[] = [
  {
    id: 'spend', title: 'Сколько можно тратить', shot: 'home',
    text: [
      'Главная отвечает на один вопрос: сколько можно потратить до ближайшего прихода, чтобы хватило на всё обязательное.',
      'Крупная сумма — это деньги на счетах минус платежи до прихода и то, что отложено в конверты и цели. Под ней — сколько выходит в день.',
      'Стрелка справа от суммы открывает «Как посчитано»: там видно, что вычли и почему.',
    ],
  },
  {
    id: 'entry', title: 'Как записать трату или приход', shot: 'entry',
    text: [
      'Нажми «Записать» на главной или «+» справа внизу, на компьютере — «Записать» над меню слева. Сверху значками выбери, что это: расход, приход, перевод, взнос в цель или платёж по долгу.',
      'Карточка под ними — счёт: листай её пальцем, чтобы сменить счёт, или нажми — откроется список. Набери сумму на клавиатуре (можно сложить: 100 + 25) и нажми категорию — частые стоят плитками первыми, ряд листается пальцем вбок, а весь список — в «Все». «Сегодня» — дата, её можно поменять в календаре.',
      'У прихода нажми категорию своего дохода, например «Зарплата», — сумма, счёт и «Ожидаемый приход» подставятся сами. Пришло другое — просто набери свою сумму поверх.',
      'С Pro можно записать голосом («кофе 300 с Т-Банка» — счёт подставится сам), по чеку или файлом выписки.',
    ],
  },
  {
    id: 'payments', title: 'Платежи и как их оплатить', shot: 'payment',
    text: [
      'Обязательные платежи — ипотека, садик, связь — живут в «Деньги → Доход и платежи». SKOKA заранее держит под них деньги.',
      'Нажми на платёж и выбери «Оплатить». Если внести больше, лишнее пойдёт в счёт следующего раза, у кредита — досрочным погашением. Если меньше — платёж будет ждать остаток.',
    ],
  },
  {
    id: 'goals', title: 'Цели', shot: 'goals',
    text: [
      'Цель — то, на что копишь: подушка, отпуск, покупка. Цели — отдельный пункт меню внизу, добавить — «+ Цель».',
      'Нажми на цель — увидишь, сколько осталось, успеваешь ли к сроку и все взносы. Отложить — кнопкой «Отложить» там же или значком «В цель» при записи: деньги закрепляются под цель, и «Можно тратить» их уже не считает.',
    ],
  },
  {
    id: 'accounts', title: 'Счета и остатки', shot: 'accounts',
    text: [
      'Все карты, наличные, накопительные и долги — в «Деньгах». Нажми на счёт — откроется его экран: что с него платится и последние операции.',
      'Остаток не сходится с банком — карандаш у суммы на экране счёта или у «На счёте сейчас» в «Операциях». Впиши, сколько на самом деле: разница запишется отдельной строкой «Корректировка», а не тратой.',
      'С Pro можно поправить все остатки разом: в том же листе «Все остатки разом» — сними главный экран приложения банка, и счета, кредитки и кредиты обновятся по нему. Ты только проверяешь.',
    ],
  },
  {
    id: 'analytics', title: 'Аналитика', shot: 'analytics',
    text: [
      'В «Аналитике» видно, куда ушли деньги: обзор периода и траты по категориям.',
      'Период считается от прихода до прихода — так видно, хватило ли денег до зарплаты.',
    ],
  },
];

/** «Частые вопросы» — по темам. */
export const FAQ: HelpGroup[] = [
  {
    title: 'Записи',
    items: [
      {
        id: 'edit', title: 'Как исправить или удалить операцию?', shot: 'operations',
        text: [
          'Открой «Операции» и нажми на операцию — откроется правка. Корзина рядом с «Сохранить» удаляет.',
          'Удалили по ошибке — внизу семь секунд видна кнопка «Вернуть».',
        ],
      },
      {
        id: 'voice', title: 'Можно записывать голосом или по чеку?',
        text: [
          'Да, с Pro. В листе записи над клавиатурой — «Чек», «Файл» и «Голосом».',
          'Голосом можно назвать и счёт: «такси 640 наличкой» — счёт «Наличные» подставится сам.',
        ],
      },
      {
        id: 'part', title: 'Нужды или свободные траты — куда записать покупку?', shot: 'part',
        text: [
          'Спроси себя: если бы денег было впритык, эта покупка всё равно была бы нужна сейчас? Да — повседневные нужды: продукты, лекарства, проезд, сломанное взамен. Нет — свободные траты: кафе, обновки, развлечения, подарки.',
          'Часть видна в листе записи под суммой, рядом с датой: у категории она задана заранее, для одной траты её можно поменять. Значок (i) у подписи «Часть плана» напомнит, что это.',
          'Одна и та же вещь бывает и тем и другим: сломалась духовка — нужды, хочется новую побольше — свободные. Таким категориям в настройке категории выбери «Бывает по-разному» — часть спросится при каждой трате.',
          'Платёж по кредиту или кредитной карте — не трата. Запиши его значком «Долг» в листе записи: он уменьшит долг и не попадёт ни в нужды, ни в свободные.',
        ],
      },
      {
        id: 'time', title: 'Почему у операции нет времени?',
        text: ['Время не было названо — например, операцию записали задним числом. SKOKA его не выдумывает. Указать можно в правке, в «Когда».'],
      },
    ],
  },
  {
    title: 'Платежи и долги',
    items: [
      {
        id: 'pay-early', title: 'Как оплатить платёж заранее?', shot: 'payment',
        text: ['Открой «Деньги → Доход и платежи» и нажми на платёж. В листе — кнопка «Оплатить». У кредита — то же на его экране: «Деньги» → нажми на кредит → «Оплатить».'],
      },
      {
        id: 'overpay', title: 'Внесено больше, чем платёж, — что будет?',
        text: [
          'У обычного платежа лишнее пойдёт в счёт следующего раза: в ленте будет видно «внесено заранее».',
          'У кредита — досрочным погашением, и в подсказке видно, каким станет долг.',
        ],
      },
      {
        id: 'prepay', title: 'Как записать досрочное погашение кредита?',
        text: ['Оплати платёж по кредиту на сумму больше графика — лишнее запишется досрочно. Или запиши значком «Долг» при записи операции.'],
      },
    ],
  },
  {
    title: 'Расчёт',
    items: [
      {
        id: 'why-less', title: 'Почему «Можно тратить» меньше, чем на счетах?', shot: 'home',
        text: [
          'Из денег на счетах вычитаются платежи до ближайшего прихода и то, что отложено в конверты и цели.',
          'Нажми на стрелку справа от суммы на главной — там «Как посчитано».',
        ],
      },
      {
        id: 'adjust', title: 'Как поправить остаток счёта?',
        text: ['«Деньги» → нажми на счёт → карандаш у суммы. Впиши, сколько на самом деле.'],
      },
    ],
  },
  {
    title: 'Pro и оплата',
    items: [
      {
        id: 'pro', title: 'Что даёт Pro?',
        text: ['Запись голосом, разбор чеков, файлов и выписок, вопросы SKOKA о твоих деньгах и разбор периода. Ручная запись и все расчёты работают и без Pro.'],
      },
      {
        id: 'renew', title: 'Как отключить продление Pro?',
        text: ['Кружок с инициалами в шапке главной (профиль) → «Подписка» — там можно отключить автопродление. Pro доработает до конца оплаченного срока.'],
      },
      {
        id: 'double', title: 'Списали дважды или Pro не открылся',
        text: ['Напиши в поддержку: шестерёнка в шапке главной → «Помощь» — разберёмся и вернём деньги.'],
      },
    ],
  },
  {
    title: 'Данные',
    items: [
      {
        id: 'phone', title: 'Как перенести данные на новый телефон?',
        text: ['Войди в свой аккаунт на новом телефоне — данные подтянутся. Если что-то не появилось, в «Профиле» есть «Загрузить с сервера».'],
      },
      {
        id: 'export', title: 'Как выгрузить операции в Excel?',
        text: ['Шестерёнка в шапке главной → «Данные» → «Выгрузить операции» — файл CSV, откроется в Excel и Google Таблицах.'],
      },
      {
        id: 'desktop', title: 'Можно пользоваться на компьютере?',
        text: [
          'Да. Открой app.skokadeneg.ru в браузере и войди в тот же аккаунт — данные будут те же, что на телефоне.',
          'На компьютере меню слева, «Записать» — над ним. Сумму можно набирать на клавиатуре, Enter записывает.',
          'Чтобы SKOKA открывалась своим окном, как программа: в Chrome и Edge — «Установить SKOKA» в адресной строке, в Safari — «Файл → Добавить в Dock».',
        ],
      },
    ],
  },
];
